// app/page.tsx
'use client';

import { useState } from 'react';
import ClientOnly from '@/components/ClientOnly';
import { 
  Sparkles, Code, Image as ImageIcon, Copy, Download, 
  Eye, Monitor, Smartphone, Tablet, ChevronRight,
  Maximize2, RotateCw, Save,
  RefreshCw, Layers, History
} from 'lucide-react';
import copy from 'clipboard-copy';
import EditableCodeArea from '@/components/EditableCodeArea';
import { convertToTarget, convertBackToHtml, type Framework } from '@/lib/bidirectional-service';

import { 
  extractHtmlFromCode, 
  extractCssFromCode,
  generatePreviewHtml 
} from '@/lib/utils';

type DeviceType = 'desktop' | 'mobile' | 'tablet';
type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';

export default function Home() {
  // 基础状态
  const [prompt, setPrompt] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 框架和预览状态
  const [framework, setFramework] = useState<Framework>('html');
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');
  
  // 双向转换状态
  const [htmlCode, setHtmlCode] = useState('');
  const [convertedCodes, setConvertedCodes] = useState<Record<Framework, string>>({
    html: '',
    react: '',
    vue: '',
    uniapp: ''
  });
  const [isConverting, setIsConverting] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [conversionHistory, setConversionHistory] = useState<Array<{
    from: string;
    to: Framework;
    timestamp: string;
    cached: boolean;
  }>>([]);
  
  // 弹窗状态
  const [showFrameworkGuide, setShowFrameworkGuide] = useState(false);
  const [activeGuide, setActiveGuide] = useState<Framework>('html');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // 生成HTML代码
  const generateCode = async () => {
    if (!prompt.trim()) {
      setError('请输入描述内容');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '生成失败');
      }

      if (data.success) {
        const newHtmlCode = data.code;
        setHtmlCode(newHtmlCode);
        setGeneratedCode(newHtmlCode);
        
        setConvertedCodes(prev => ({
          ...prev,
          html: newHtmlCode,
          react: '',
          vue: '',
          uniapp: ''
        }));
        
        setFramework('html');
        
        setConversionHistory(prev => [{
          from: '用户输入',
          to: 'html',
          timestamp: new Date().toLocaleTimeString(),
          cached: false
        }, ...prev.slice(0, 9)]);
      }
    } catch (err: any) {
      setError(err.message || '请求失败，请重试');
      console.error('生成错误:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 正向转换：HTML → 目标框架
  const convertToFramework = async (targetFramework: Framework) => {
    if (!htmlCode) return;
    
    setIsConverting(true);
    setSyncStatus('syncing');

    try {
      const result = await convertToTarget(htmlCode, targetFramework);
      
      setConvertedCodes(prev => ({
        ...prev,
        [targetFramework]: result.code
      }));
      
      setGeneratedCode(result.code);
      setFramework(targetFramework);
      
      setConversionHistory(prev => [{
        from: 'HTML',
        to: targetFramework,
        timestamp: new Date().toLocaleTimeString(),
        cached: result.cached
      }, ...prev.slice(0, 9)]);
      
      setSyncStatus('synced');
      setTimeout(() => setSyncStatus('idle'), 2000);
      
    } catch (err: any) {
      setError(err.message || '转换失败，请重试');
      console.error('转换错误:', err);
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 3000);
    } finally {
      setIsConverting(false);
    }
  };

  // 处理代码编辑
  const handleCodeEdit = async (newCode: string, editedFramework: Framework) => {
    if (editedFramework === 'html') {
      // 编辑HTML代码
      setHtmlCode(newCode);
      setGeneratedCode(newCode);
      
      setConvertedCodes(prev => ({
        ...prev,
        react: '',
        vue: '',
        uniapp: ''
      }));
      
      setFramework('html');
      
      setSyncStatus('synced');
      setTimeout(() => setSyncStatus('idle'), 1500);
      
    } else {
      // 编辑目标框架代码，需要反向转换
      setIsConverting(true);
      setSyncStatus('syncing');

      try {
        const result = await convertBackToHtml(newCode, editedFramework);
        
        setHtmlCode(result.code);
        setGeneratedCode(newCode);
        
        setConvertedCodes(prev => ({
          ...prev,
          [editedFramework]: newCode,
          html: result.code
        }));
        
        setFramework(editedFramework);
        
        setConversionHistory(prev => [{
          from: editedFramework.toUpperCase(),
          to: 'html',
          timestamp: new Date().toLocaleTimeString(),
          cached: result.cached
        }, ...prev.slice(0, 9)]);
        
        setSyncStatus('synced');
        setTimeout(() => setSyncStatus('idle'), 1500);
        
      } catch (error: any) {
        console.error('反向转换失败:', error);
        setError('同步失败: ' + error.message);
        setSyncStatus('error');
        setTimeout(() => setSyncStatus('idle'), 3000);
      } finally {
        setIsConverting(false);
      }
    }
  };

  // 提取CSS代码
const extractCssForPreview = (fullCode: string): string => {
  try {
    const styleMatches = fullCode.match(/<style[\s\S]*?>([\s\S]*?)<\/style>/i);
    if (styleMatches && styleMatches[1]) {
      return styleMatches[1].trim();
    }
    return '';
  } catch (error) {
    console.error('提取CSS失败:', error);
    return '';
  }
};

// 提取HTML代码（去除<style>和<script>标签用于预览）
const extractHtmlForPreview = (fullCode: string): string => {
  try {
    // 移除<style>标签内容
    let html = fullCode.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');
    // 移除<script>标签内容
    html = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
    // 移除注释
    html = html.replace(/<!--[\s\S]*?-->/g, '');
    return html.trim();
  } catch (error) {
    console.error('提取HTML失败:', error);
    return fullCode;
  }
};


  // 复制代码
  const copyToClipboard = async () => {
    if (!generatedCode) return;
    try {
      await copy(generatedCode);
      alert('代码已复制到剪贴板！');
    } catch (err) {
      console.error('复制失败:', err);
    }
  };


  


  // 下载代码
  const downloadCode = () => {
    if (!generatedCode) return;
    const extension = framework === 'html' ? '.html' : 
                     framework === 'react' ? '.jsx' : '.vue';
    const blob = new Blob([generatedCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `generated-code${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };






  // HTML预览函数
  // 更新预览函数
  const openHtmlPreview = () => {
    if (!generatedCode) return;
    
    const previewHtml = extractHtmlFromCode(
      framework === 'html' ? generatedCode : htmlCode
    );
    const previewCss = extractCssFromCode(
      framework === 'html' ? generatedCode : htmlCode
    );
    
    const htmlContent = generatePreviewHtml(previewHtml, previewCss, deviceType);
    
    const previewWindow = window.open('', '_blank', 'width=1400,height=800');
    if (previewWindow) {
      previewWindow.document.write(htmlContent);
      previewWindow.document.close();
    }
  };
  // 返回HTML视图
  const switchToHtml = () => {
    if (!htmlCode) return;
    setGeneratedCode(htmlCode);
    setFramework('html');
  };

  // 获取同步状态文本
  const getSyncStatusText = () => {
    switch (syncStatus) {
      case 'syncing': return '🔄 同步中...';
      case 'synced': return '✅ 同步完成';
      case 'error': return '❌ 同步失败';
      default: return '就绪';
    }
  };

  // 获取框架图标
  const getFrameworkIcon = (fw: Framework) => {
    switch (fw) {
      case 'html': return '🌐';
      case 'react': return '⚛️';
      case 'vue': return '🟢';
      case 'uniapp': return '📱';
      default: return '📄';
    }
  };

  return (
    <div suppressHydrationWarning>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* 头部 */}
          <header className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Code className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                AI慧行慧
              </h1>
            </div>

          </header>

          {/* 三栏布局 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 左侧栏：输入区域 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                {/* 描述输入 */}
                <div className="mb-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-500" />
                    描述需求
                  </h2>
                  
                  <textarea
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      setError('');
                    }}
                    placeholder="例如：生成一个现代化的登录页面，包含邮箱、密码输入框和登录按钮..."
                    className="w-full h-32 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    disabled={isLoading}
                  />
                  
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                    <ImageIcon className="w-4 h-4" />
                    描述越详细，生成的代码越准确
                  </div>
                </div>

                {/* 框架选择 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    当前框架
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* 'react', 'vue', */}
                    {(['html',  'uniapp'] as Framework[]).map((fw) => (
                      <div
                        key={fw}
                        onClick={() => {
                          if (fw === 'html') {
                            switchToHtml();
                          } else if (convertedCodes[fw]) {
                            setGeneratedCode(convertedCodes[fw]);
                            setFramework(fw);
                          }
                        }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          framework === fw
                            ? 'border-blue-500 bg-blue-50'
                            : convertedCodes[fw]
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xl">{getFrameworkIcon(fw)}</span>
                          {fw === 'html' && (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                              可预览
                            </span>
                          )}
                        </div>
                        <div className="font-medium mt-1 text-sm">{fw.toUpperCase()}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {fw === 'html' ? '基础HTML' : 
                          fw === 'react' ? 'React组件' :
                          fw === 'vue' ? 'Vue组件' : 'uni-app'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 框架转换控制 */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      框架转换
                    </label>
                    <div className={`text-xs px-2 py-1 rounded ${
                      syncStatus === 'syncing' ? 'bg-blue-100 text-blue-700' :
                      syncStatus === 'synced' ? 'bg-green-100 text-green-700' :
                      syncStatus === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getSyncStatusText()}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                   {/* 'react', 'vue',  */}
                    {(['uniapp'] as Framework[]).map((fw) => (
                      <button
                        key={fw}
                        onClick={() => convertToFramework(fw)}
                        disabled={!htmlCode || isConverting}
                        className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                          convertedCodes[fw] && framework === fw
                            ? 'bg-green-600 text-white'
                            : convertedCodes[fw]
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } disabled:opacity-50 text-sm`}
                      >
                        <span className="text-base">{getFrameworkIcon(fw)}</span>
                        {convertedCodes[fw] ? '已转换' : `转${fw.toUpperCase()}`}
                        {isConverting && framework === fw && (
                          <RotateCw className="w-3 h-3 animate-spin" />
                        )}
                      </button>
                    ))}
                    
                    {framework !== 'html' && htmlCode && (
                      <button
                        onClick={switchToHtml}
                        className="px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg flex items-center gap-2 text-sm"
                      >
                        <span className="text-base">🌐</span>
                        返回HTML
                      </button>
                    )}
                  </div>
                  
                  {/* 转换状态 */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm text-gray-700 flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        已转换: {Object.keys(convertedCodes).filter(fw => 
                          fw !== 'html' && convertedCodes[fw as Framework]
                        ).length} / 3
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {framework !== 'html' ? '编辑将自动同步回HTML' : '编辑HTML将重置其他框架'}
                    </div>
                  </div>
                </div>

                {/* 设备预览选择器 */}
                {framework === 'html' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      预览设备
                    </label>
                    <div className="flex gap-2">
                      {[
                        { id: 'desktop', name: '桌面', icon: <Monitor size={16} /> },
                        { id: 'tablet', name: '平板', icon: <Tablet size={16} /> },
                        { id: 'mobile', name: '手机', icon: <Smartphone size={16} /> },
                      ].map((device) => (
                        <button
                          key={device.id}
                          onClick={() => setDeviceType(device.id as DeviceType)}
                          className={`flex-1 flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${
                            deviceType === device.id
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-700'
                          }`}
                        >
                          {device.icon}
                          <span className="mt-2 text-sm">{device.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 错误显示 */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {/* 生成按钮 */}
                <button
                  onClick={generateCode}
                  disabled={isLoading || !prompt.trim()}
                  className={`w-full py-3 text-lg font-semibold rounded-xl flex items-center justify-center gap-3 ${
                    isLoading || !prompt.trim()
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90'
                  } text-white transition-all`}
                >
                  {isLoading ? (
                    <>
                      <RotateCw className="w-5 h-5 animate-spin" />
                      生成中...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      生成代码
                    </>
                  )}
                </button>

                {/* 操作按钮组 */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button
                    onClick={() => {
                      if (framework === 'html') {
                        openHtmlPreview();
                      } else {
                        setActiveGuide(framework);
                        setShowFrameworkGuide(true);
                      }
                    }}
                    disabled={!generatedCode}
                    className="px-3 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    {framework === 'html' ? '预览' : '指南'}
                  </button>
                  
                  <button
                    onClick={copyToClipboard}
                    disabled={!generatedCode}
                    className="px-3 py-2.5 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    复制
                  </button>
                </div>
              </div>

              {/* 转换历史记录 */}
              {conversionHistory.length > 0 && (
                <div className="mt-6 bg-white rounded-xl shadow p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    转换历史
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {conversionHistory.map((record, index) => (
                      <div 
                        key={index} 
                        className="text-sm p-2 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">
                            {record.from} → {record.to.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">
                            {record.timestamp}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                          {record.cached ? (
                            <span className="text-green-600">✓ 缓存</span>
                          ) : (
                            <span className="text-blue-600">⟳ AI生成</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 中间栏：实时预览区域 */}
            <div className="lg:col-span-1">
            
              <ClientOnly fallback={
              <div className="bg-white rounded-2xl shadow-lg h-[60em] flex items-center justify-center">
                <div className="text-gray-400">加载预览...</div>
              </div>
            }>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden" style={{height: '60em'}}>

                     <div className="bg-white rounded-2xl shadow-lg overflow-hidden" style= {{height:60+'em'}}>
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">实时预览</h3>
                    <div className="flex items-center gap-2">
                      {framework === 'html' && generatedCode && (
                        <button
                          onClick={() => setShowPreviewModal(true)}
                          className="px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-sm flex items-center gap-2"
                        >
                          <Maximize2 size={14} />
                          全屏
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-4 h-[calc(100%-80px)]">
                  {generatedCode ? (
                    <div className="h-full border border-gray-200 rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <meta charset="UTF-8">
                          <meta name="viewport" content="width=device-width, initial-scale=1.0">
                          <style>
                            ${extractCssForPreview(framework === 'html' ? generatedCode : htmlCode)}
                            body { 
                              margin: 0; 
                              padding: 20px; 
                              background: #f8fafc; 
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                            }
                            .preview-container { 
                              max-width: ${deviceType === 'mobile' ? '375px' : deviceType === 'tablet' ? '768px' : '1200px'};
                              margin: 0 auto;
                              background: white;
                              border-radius: 8px;
                              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                              overflow: hidden;
                            }
                            @media (max-width: 768px) {
                              body { padding: 10px; }
                            }
                          </style>
                        </head>
                        <body>
                          <div class="preview-container">
                            ${extractHtmlForPreview(framework === 'html' ? generatedCode : htmlCode)}
                          </div>
                        </body>
                        </html>
                      `}
                      className="w-full h-full border-0"
                      title="实时预览"
                      sandbox="allow-same-origin allow-scripts"
                    />
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <div className="text-4xl mb-4">
                        {getFrameworkIcon(framework)}
                      </div>
                      <p className="text-lg font-medium mb-2">
                        {!generatedCode ? '等待生成代码' : `${framework.toUpperCase()} 预览`}
                      </p>
                      <p className="text-sm text-gray-500 text-center">
                        {!generatedCode 
                          ? '输入描述并生成代码'
                          : framework !== 'html' 
                            ? '此框架需转换为HTML预览'
                            : '正在加载预览...'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </ClientOnly>
            
            
            
           
            </div>

            {/* 右侧栏：代码区域 */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900 rounded-2xl shadow-lg overflow-hidden" style={{height: '60em'}}>
                {/* 代码编辑器区域 */}
          
                  <EditableCodeArea
                    code={generatedCode}
                    framework={framework}
                    fileName={
                      framework === 'html' ? 'index' : 
                      framework === 'react' ? 'Component' :
                      framework === 'vue' ? 'Component' : 
                      'Page'
                    }
                    onCodeChange={handleCodeEdit}
                    isConverting={isConverting}
                    readOnly={!generatedCode || isLoading}
                  />
              </div>

              {/* 特性卡片 */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="text-blue-500 text-xl mb-2">🔄</div>
                  <h4 className="font-semibold text-sm mb-1">双向同步</h4>
                  <p className="text-gray-600 text-xs">编辑自动同步</p>
                </div>
                
                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="text-green-500 text-xl mb-2">⚡</div>
                  <h4 className="font-semibold text-sm mb-1">智能缓存</h4>
                  <p className="text-gray-600 text-xs">避免重复转换</p>
                </div>
                
                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="text-purple-500 text-xl mb-2">💻</div>
                  <h4 className="font-semibold text-sm mb-1">实时编辑</h4>
                  <p className="text-gray-600 text-xs">撤销/重做功能</p>
                </div>
                
                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="text-orange-500 text-xl mb-2">📋</div>
                  <h4 className="font-semibold text-sm mb-1">多框架</h4>
                  <p className="text-gray-600 text-xs">4种框架支持</p>
                </div>
              </div>
            </div>
          </div>

          {/* 底部信息 */}
          <div className="mt-8 p-4 bg-blue-50 rounded-xl text-center">
            <code className="text-sm text-blue-700">
              版本: Next.js 14 + HTML + CSS + TypeScript + DeepSeek AI
            </code>
          </div>
        </div>

        {/* 全屏预览弹窗 */}
        {showPreviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <div className="flex items-center gap-4">
                  <h3 className="text-xl font-semibold">全屏预览</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="px-3 py-1 bg-gray-100 rounded-full">
                      设备: {deviceType === 'mobile' ? '📱 手机' : deviceType === 'tablet' ? '📱 平板' : '💻 桌面'}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                      HTML  + CSS
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const previewWindow = window.open('', '_blank');
                      if (previewWindow) {
                        previewWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <meta charset="UTF-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <script src="https://cdn.tailwindcss.com"></script>
                          </head>
                          <body style="margin:0;padding:20px;background:#f8fafc;">
                            <div style="max-width:${deviceType === 'mobile' ? '375px' : deviceType === 'tablet' ? '768px' : '100%'};margin:0 auto;">
                              ${framework === 'html' ? generatedCode : htmlCode}
                            </div>
                          </body>
                          </html>
                        `);
                        previewWindow.document.close();
                      }
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm flex items-center gap-2"
                  >
                    <Maximize2 size={16} />
                    新窗口
                  </button>
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                  >
                    关闭
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <meta charset="UTF-8">
                      <meta name="viewport" content="width=device-width, initial-scale=1.0">
                      <script src="https://cdn.tailwindcss.com"></script>
                      <style>
                        body { margin: 0; padding: 20px; background: #f8fafc; }
                        .preview-container { 
                          max-width: ${deviceType === 'mobile' ? '375px' : deviceType === 'tablet' ? '768px' : '100%'};
                          margin: 0 auto;
                        }
                      </style>
                    </head>
                    <body>
                      <div class="preview-container">
                        ${framework === 'html' ? generatedCode : htmlCode}
                      </div>
                    </body>
                    </html>
                  `}
                  className="w-full h-full border-0"
                  title="全屏预览"
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>
            </div>
          </div>
        )}

        {/* 框架使用指南弹窗 */}
        {showFrameworkGuide && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">
                    {activeGuide === 'react' && '⚛️ React 使用指南'}
                    {activeGuide === 'vue' && '🟢 Vue 使用指南'}
                    {activeGuide === 'uniapp' && '📱 uni-app 使用指南'}
                  </h3>
                  <button
                    onClick={() => setShowFrameworkGuide(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {activeGuide === 'react' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">📦 安装依赖</h4>
                      <code className="text-sm bg-blue-100 text-blue-900 p-2 rounded block">
                        npm install react react-dom tailwindcss
                      </code>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2">🚀 使用方法</h4>
                      <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                        <li>复制生成的代码到 React 项目</li>
                        <li>创建新组件文件（如 Component.jsx）</li>
                        <li>粘贴代码并保存</li>
                        <li>在其他组件中导入使用</li>
                        <li className="text-blue-600 font-medium">✨ 双向同步：编辑此代码会自动更新HTML版本</li>
                      </ol>
                    </div>
                  </div>
                )}
                
                {activeGuide === 'vue' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2">🟢 Vue 使用指南</h4>
                      <p className="text-gray-700 mb-3">
                        生成的代码是 Vue 3 单文件组件，使用组合式 API 和 Tailwind CSS。
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-gray-600">
                        <li>复制代码到 .vue 文件中</li>
                        <li>在Vue项目中导入组件</li>
                        <li className="text-blue-600 font-medium">✨ 双向同步：编辑此代码会自动更新HTML版本</li>
                      </ul>
                    </div>
                  </div>
                )}
                
                {activeGuide === 'uniapp' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-medium text-purple-800 mb-2">📱 uni-app 使用指南</h4>
                      <p className="text-gray-700 mb-3">
                        生成的代码适用于 uni-app 框架，可用于开发微信小程序、App 等。
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-gray-600">
                        <li>在HBuilderX中创建uni-app项目</li>
                        <li>将代码保存为 .vue 文件</li>
                        <li>在 pages.json 中配置路由</li>
                        <li className="text-blue-600 font-medium">✨ 双向同步：编辑此代码会自动更新HTML版本</li>
                      </ul>
                    </div>
                  </div>
                )}
                
                <div className="mt-6 pt-6 border-t">
                  <button
                    onClick={() => setShowFrameworkGuide(false)}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    我明白了
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}